// Global Types
import type {
  $FullAdminRegatta,
  $FullRegattaEntry,
  $FullRegattaGroup,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
  countries,
  docRoute,
  docType,
} from 'common.enums';
import {
  GroupType,
  RatingType,
  RegattaEntryCrewmemberGender,
  RegattaEntryCrewmemberTshirt,
  RegattaGroupRatingType,
  RegattaRequirementType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Col,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  AvField,
  AvForm,
  AvInput,
} from 'availity-reactstrap-validation';
import {
  ReactSortable,
} from 'react-sortablejs';
import {
  InputFile,
  InputNumber,
  InputTypeahead,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  cleanFormValues,
  getValue,
  isDiff,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  getFullItemUrl,
} from 'common.helpers/dist/browser';
import {
  i18next,
  regattaEntryCrewmemberGenderLabel,
  regattaEntryCrewmemberTshirtLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import InputNo from '../../form/InputNo';
import InputNoTypeahead from '../../form/InputNoTypeahead';
import InputYesNo from '../../form/InputYesNo';
import RegattaGroupSection from '../../sections/RegattaEntriesPageSections/RegattaGroupSection';
import RegattaEntriesStatsSection from '../../sections/RegattaEntriesPageSections/RegattaEntriesStatsSection';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';
import type {
  $RegattaGroupBundle,
} from '../../../types';

type $UpsertRegattaEntry = $ModelUpsertParams<'RegattaEntry'> & {
  RegattaEntryCrewmembers: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>>;
  RegattaEntryRequirements: Array<$ModelUpsertParams<'RegattaEntryRequirement'>>;
};

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  activeRegattaGroup: $FullRegattaGroup | null;
  data: Array<$FullRegattaEntry>;
  loading: boolean;
  mainItem: $FullAdminRegatta | null;
  upsertItem: $UpsertRegattaEntry | null;
};

const pageId = adminPages.regattaEntriesPage;

class RegattaEntriesPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegattaGroup: null,
      data: [],
      loading: false,
      mainItem: null,
      upsertItem: null,
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'RegattaEntry') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  getEntryRegattaGroup(RegattaGroupId: null | string): $FullRegattaGroup | null {
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const regattaGroup = _.find(
        mainItem.RegattaGroups,
        {
          id: RegattaGroupId,
        },
      ) as $FullRegattaGroup | undefined;

      return regattaGroup || null;
    }

    return null;
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneRegatta({
            id: RegattaId,
          });
          const data = await Services.Api.getManyRegattaEntry({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async findRegattaEntryCrewmembers(
    search: string,
  ): Promise<Array<$Model<'RegattaEntryCrewmember'>>> {
    const {
      upsertItem,
    } = this.state;

    let output: Array<$Model<'RegattaEntryCrewmember'>> = [];

    try {
      if (upsertItem !== null && upsertItem.EntryId) {
        output = await Services.Api.findManyRegattaEntryCrewmember({
          EntryId: upsertItem.EntryId,
          search,
        });
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }

    return output;
  }

  /* eslint-disable-next-line complexity */
  renderUpsertRegattaEntryPart() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      mainItem,
      upsertItem,
    } = this.state;

    const activeRegattaGroupType = _.get(
      activeRegattaGroup,
      'Group.Type',
    );

    const isSportGroupType: boolean = activeRegattaGroupType === GroupType.sport;
    const isCruiserGroupType: boolean = activeRegattaGroupType === GroupType.cruiser;

    return ((upsertItem !== null && mainItem !== null) ? (
      <>
        <Row>
          <Col
            lg={3}
            md={4}
          >
            <AvField
              label={t('Group')}
              name={'RegattaGroupId'}
              onChange={(e) => this.setState({
                activeRegattaGroup: this.getEntryRegattaGroup(e.target.value),
              })}
              required
              type={'select'}
              value={upsertItem.RegattaGroupId}
            >
              {(upsertItem.RegattaGroupId === '' && activeRegattaGroup === null) && (
                <option />
              )}
              {_.map(
                mainItem.RegattaGroups,
                (i) => (
                  <option
                    key={i.id}
                    value={i.id}
                  >
                    {i.AltName || _.get(
                      i,
                      'Group.Name',
                      '',
                    )}
                  </option>
                ),
              )}
            </AvField>
          </Col>
          {mainItem.CountryEnabled ? (
            <>
              <Col
                lg={2}
                md={4}
                xs={6}
              >
                <AvField
                  label={t('Country')}
                  name={'RegistrationNo'}
                  required
                  type={'select'}
                  value={upsertItem.RegistrationNo}
                >
                  {upsertItem.RegistrationNo === '' && (
                    <option />
                  )}
                  {_.map(
                    countries,
                    (i) => (
                      <option
                        key={i.code}
                        value={`${i.code}-0`}
                      >
                        {i.name}
                      </option>
                    ),
                  )}
                </AvField>
              </Col>
              <Col
                lg={2}
                md={4}
                xs={6}
              >
                <InputNo
                  label={t('Sail no.')}
                  name={'SailNo'}
                  value={upsertItem.SailNo}
                />
              </Col>
            </>
          ) : (
            <>
              {isSportGroupType && (
                <Col
                  lg={2}
                  md={4}
                  xs={6}
                >
                  <InputNo
                    label={t('Sail no.')}
                    name={'RegistrationNo'}
                    value={upsertItem.RegistrationNo}
                  />
                </Col>
              )}
              {isCruiserGroupType && (
                <Col
                  lg={2}
                  md={4}
                  xs={6}
                >
                  <InputNoTypeahead
                    getOptions={async (search: string) => {
                      try {
                        const data = await Services.Api.findManyEntry({
                          RegistrationNo: search || '',
                        });

                        return data.map((
                          dataItem: $Model<'Entry'>,
                        ): $Model<'Entry'> & { OrigName?: null | string } => ({
                          ...dataItem,
                          Name: `${dataItem.RegistrationNo} ${dataItem.Name || ''}`,
                          OrigName: dataItem.Name,
                        }));
                      } catch (error) {
                        ErrorLog.exception(error as Error);
                      }

                      return [];
                    }}
                    label={isCruiserGroupType ? t('Registration no.') : t('Sail no.')}
                    name={'RegistrationNo'}
                    onOptionSelect={(option: ($Model<'Entry'> & { OrigName?: string }) | null) => {
                      if (option) {
                        this.setState({
                          upsertItem: {
                            ...upsertItem,
                            Club: getValue(
                              upsertItem.Club,
                              option.Club,
                            ),
                            EntryId: option.id,
                            Harbor: getValue(
                              upsertItem.Harbor,
                              option.Harbor,
                            ),
                            Image: getValue(
                              upsertItem.Image,
                              option.Image,
                            ),
                            Model: getValue(
                              upsertItem.Model,
                              option.Model,
                            ),
                            Name: getValue(
                              upsertItem.Name,
                              option.OrigName,
                            ),
                          },
                        });
                      } else {
                        this.setState({
                          upsertItem: {
                            ...upsertItem,
                            EntryId: null,
                          },
                        });
                      }
                    }}
                    required
                    valid={upsertItem.EntryId !== null}
                    value={upsertItem.RegistrationNo}
                  />
                </Col>
              )}
            </>
          )}
          {isCruiserGroupType && (
            <Col
              lg={3}
              md={6}
              xs={6}
            >
              <AvField
                autoComplete={'new-password'}
                label={t('Title')}
                name={'Name'}
                required={isCruiserGroupType}
                value={upsertItem.Name}
              />
            </Col>
          )}
          <Col
            lg={1}
            md={2}
            xs={3}
          >
            <AvField
              disabled={!mainItem.BowNoEnabled}
              label={t('Bow no.')}
              name={'BowNo'}
              value={upsertItem.BowNo}
            />
          </Col>
          <Col
            lg={1}
            md={2}
            xs={3}
          >
            <AvField
              label={t('Tracker ID')}
              min={1}
              name={'TrackerId'}
              type={'number'}
              value={upsertItem.TrackerId}
            />
          </Col>
        </Row>
        <Row>
          <Col
            lg={3}
            md={6}
            xs={6}
          >
            <InputFile
              fileDirUrl={`${config.CDN_URL}/images/max`}
              imageThumbDirUrl={`${config.CDN_URL}/images/min`}
              label={t('Image')}
              labelChangeFile={t('Change file')}
              labelError={t('Error')}
              labelFile={t('File')}
              name={'Image'}
              onError={ErrorLog.exception}
              uploadUrl={`${config.CDN_URL}/v1.0/upload/image`}
              value={upsertItem.Image}
            />
          </Col>
          {isSportGroupType ? (
            <Col
              lg={3}
              md={6}
              xs={6}
            >
              <AvField
                label={t('Harbor')}
                name={'Harbor'}
                required={mainItem.RegistrationHarborField}
                value={upsertItem.Harbor}
              />
            </Col>
          ) : (
            <>
              <Col
                lg={3}
                md={6}
                xs={6}
              >
                <AvField
                  label={t('Make / Model')}
                  name={'Model'}
                  value={upsertItem.Model}
                />
              </Col>
              <Col
                lg={3}
                md={6}
                xs={6}
              >
                <AvField
                  label={t('Harbor')}
                  name={'Harbor'}
                  required={mainItem.RegistrationHarborField}
                  value={upsertItem.Harbor}
                />
              </Col>
            </>
          )}
          <Col
            lg={3}
            md={6}
            xs={6}
          >
            <AvField
              label={t('Club')}
              name={'Club'}
              required={mainItem.RegistrationClubField}
              value={upsertItem.Club}
            />
          </Col>
        </Row>
      </>
    ) : null);
  }

  renderUpsertRegattaEntryCoachPart() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      upsertItem,
    } = this.state;

    return (upsertItem !== null
      && activeRegattaGroup !== null
      && activeRegattaGroup.CoachEnabled === true ? (
        <Row>
          <Col>
            <AvField
              label={t('Coach name')}
              name={'CoachName'}
              value={upsertItem.CoachName}
            />
          </Col>
          <Col>
            <AvField
              label={t('Coach email')}
              name={'CoachEmail'}
              type={'email'}
              value={upsertItem.CoachEmail}
            />
          </Col>
          <Col>
            <AvField
              label={t('Coach phone')}
              name={'CoachPhone'}
              value={upsertItem.CoachPhone}
            />
          </Col>
        </Row>
      ) : null);
  }

  /* eslint-disable-next-line complexity */
  renderUpsertRegattaEntryCrewmembersPartListItem(
    regattaEntryCrewmember: $ModelUpsertParams<'RegattaEntryCrewmember'>,
    index: number,
  ) {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      mainItem,
      upsertItem,
    } = this.state;

    const crewQuantity: number = _.get(
      activeRegattaGroup,
      'Group.CrewQuantity',
      0,
    );

    return (mainItem !== null ? (
      <ListGroupItem className={'p-0 border-0'}>
        {index === 0 && (
          <Label>
            {t('Skipper')}
          </Label>
        )}
        {index === 1 && (
          <Label>
            {t('Crew')}
          </Label>
        )}
        <Row>
          <Col
            lg={7}
            md={12}
          >
            {regattaEntryCrewmember.id && (
              <AvInput
                name={`RegattaEntryCrewmembers[${index}]id`}
                style={{
                  display: 'none',
                }}
                value={regattaEntryCrewmember.id}
              />
            )}
            <AvInput
              name={`RegattaEntryCrewmembers[${index}]RegattaEntryId`}
              style={{
                display: 'none',
              }}
              value={regattaEntryCrewmember.RegattaEntryId}
            />
            <AvInput
              name={`RegattaEntryCrewmembers[${index}]SortId`}
              style={{
                display: 'none',
              }}
              value={index + 1}
            />
            <Row className={'no-gutters'}>
              <Col
                lg={6}
                md={6}
                xl={''}
                xs={6}
              >
                <InputTypeahead
                  className={'mb-xl-1'}
                  getOptions={(search: string) => this.findRegattaEntryCrewmembers(search)}
                  label={index === 0 ? t('Firstname') : ''}
                  name={`RegattaEntryCrewmembers[${index}]_Firstname`}
                  onChange={(v) => {
                    if (upsertItem !== null) {
                      const {
                        RegattaEntryCrewmembers,
                      } = upsertItem;

                      if (RegattaEntryCrewmembers[index]) {
                        RegattaEntryCrewmembers[index].Name = v;
                      }

                      this.setState({
                        upsertItem: {
                          ...upsertItem,
                          RegattaEntryCrewmembers,
                        },
                      });
                    }
                  }}
                  onOptionSelect={(option: $Model<'RegattaEntryCrewmember'> | undefined) => {
                    if (option) {
                      const appliedItem = {
                        ...regattaEntryCrewmember,
                        Birthdate: option.Birthdate,
                        Email: option.Email,
                        Gender: option.Gender,
                        Membership: option.Membership,
                        Name: option.Name,
                        Phone: option.Phone,
                        Qualification: option.Qualification,
                        Tshirt: option.Tshirt,
                        Weight: option.Weight,
                      };

                      if (upsertItem !== null) {
                        upsertItem.RegattaEntryCrewmembers[index] = appliedItem;

                        this.setState({
                          upsertItem,
                        });
                      }

                      return appliedItem.Name.split(/ (.+)/)[0];
                    }

                    return '';
                  }}
                  required
                  value={regattaEntryCrewmember.Name.split(/ (.+)/)[0]}
                />
              </Col>
              <Col
                lg={6}
                md={6}
                xl={''}
                xs={6}
              >
                <AvField
                  className={'mb-xl-1'}
                  label={index === 0 && t('Lastname')}
                  name={`RegattaEntryCrewmembers[${index}]_Lastname`}
                  required
                  value={regattaEntryCrewmember.Name.split(/ (.+)/)[1]}
                />
              </Col>
              <Col
                lg={4}
                md={4}
                xl={''}
                xs={6}
              >
                <AvField
                  className={'mb-xl-1'}
                  label={index === 0 && t('Email')}
                  name={`RegattaEntryCrewmembers[${index}]Email`}
                  required={index === 0}
                  type={'email'}
                  value={regattaEntryCrewmember.Email}
                />
              </Col>
              <Col
                lg={4}
                md={4}
                xl={''}
                xs={3}
              >
                <AvField
                  className={'mb-xl-1'}
                  label={index === 0 && t('Phone')}
                  name={`RegattaEntryCrewmembers[${index}]Phone`}
                  value={regattaEntryCrewmember.Phone}
                />
              </Col>
              <Col
                lg={4}
                md={4}
                xl={''}
                xs={3}
              >
                <AvField
                  className={'mb-xl-1'}
                  label={index === 0 && t('Birthdate')}
                  name={`RegattaEntryCrewmembers[${index}]Birthdate`}
                  required={mainItem.RegistrationBirthdateField}
                  type={'date'}
                  value={regattaEntryCrewmember.Birthdate}
                />
              </Col>
            </Row>
          </Col>
          <Col
            className={'mb-xl-0 mb-5'}
            lg={''}
            md={12}
          >
            <Row>
              <Col
                lg={12}
                xl={6}
              >
                <Row className={'no-gutters'}>
                  <Col>
                    <InputNumber
                      className={'mb-xl-1'}
                      label={index === 0 ? t('Weight') : undefined}
                      name={`RegattaEntryCrewmembers[${index}]Weight`}
                      required={mainItem.RegistrationWeightField}
                      value={regattaEntryCrewmember.Weight}
                    />
                  </Col>
                  <Col>
                    <AvField
                      className={'select-height mb-xl-1'}
                      label={index === 0 && t('Gender')}
                      name={`RegattaEntryCrewmembers[${index}]Gender`}
                      type={'select'}
                      value={regattaEntryCrewmember.Gender}
                    >
                      {regattaEntryCrewmember.Gender === null && (
                        <option />
                      )}
                      {_.map(
                        RegattaEntryCrewmemberGender,
                        (i) => (
                          <option
                            key={i}
                            value={i}
                          >
                            {t(regattaEntryCrewmemberGenderLabel[i])}
                          </option>
                        ),
                      )}
                    </AvField>
                  </Col>
                  <Col>
                    <AvField
                      className={'select-height mb-xl-1'}
                      label={index === 0 && t('Tshirt')}
                      name={`RegattaEntryCrewmembers[${index}]Tshirt`}
                      required={mainItem.RegistrationTshirtField}
                      type={'select'}
                      value={regattaEntryCrewmember.Tshirt}
                    >
                      {regattaEntryCrewmember.Tshirt === null && (
                        <option />
                      )}
                      {_.map(
                        RegattaEntryCrewmemberTshirt,
                        (i) => (
                          <option
                            key={i}
                            value={i}
                          >
                            {t(regattaEntryCrewmemberTshirtLabel[i])}
                          </option>
                        ),
                      )}
                    </AvField>
                  </Col>
                </Row>
              </Col>
              <Col
                lg={12}
                xl={6}
              >
                <Row className={'no-gutters'}>
                  <Col xs={5}>
                    <AvField
                      label={index === 0 && t('Qualification')}
                      name={`RegattaEntryCrewmembers[${index}]Qualification`}
                      value={regattaEntryCrewmember.Qualification}
                    />
                  </Col>
                  <Col xs={4}>
                    <AvField
                      label={index === 0 && t('Membership')}
                      name={`RegattaEntryCrewmembers[${index}]Membership`}
                      required={mainItem.RegistrationMembershipField}
                      value={regattaEntryCrewmember.Membership}
                    />
                  </Col>
                  <Col
                    className={'text-right'}
                    xs={3}
                  >
                    {crewQuantity === 0 && (
                      <Button
                        className={'ml-1'}
                        color={'danger'}
                        id={'delete-regatta-entry-crewmember-button'}
                        onClick={() => {
                          if (upsertItem !== null && upsertItem.RegattaEntryCrewmembers) {
                            const updatedRegattaCrewmembers = [
                              ...upsertItem.RegattaEntryCrewmembers,
                            ];

                            updatedRegattaCrewmembers.splice(
                              index,
                              1,
                            );

                            upsertItem.RegattaEntryCrewmembers = updatedRegattaCrewmembers;
                          }

                          this.setState({
                            upsertItem,
                          });
                        }}
                        outline
                        style={{
                          display: index === 0 ? 'none' : '',
                          marginTop: 1,
                        }}
                      >
                        <em className={'fa icon-trash'} />
                      </Button>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </ListGroupItem>
    ) : null);
  }

  renderUpsertRegattaEntryCrewmembersPartListList(
    regattaEntryCrewmembers: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>>,
  ) {
    const {
      upsertItem,
    } = this.state;

    if (window && window.screen.width > 576) {
      const list = regattaEntryCrewmembers.map((
        regattaEntryCrewmember: $ModelUpsertParams<'RegattaEntryCrewmember'>,
      ) => ({
        id: regattaEntryCrewmember.SortId,
        name: regattaEntryCrewmember.Name,
      }));

      return (
        <ListGroup flush>
          <ReactSortable
            list={list}
            setList={(newList) => {
              if (newList.length > 1) {
                const updatedRegattaEntryCrewmembers: Array<
                $ModelUpsertParams<'RegattaEntryCrewmember'>
                > = newList
                  .map((listItem, index) => {
                    const item = _.find(
                      regattaEntryCrewmembers,
                      {
                        SortId: listItem.id,
                      },
                    );

                    if (item) {
                      item.SortId = index + 1;
                    }

                    return item;
                  });

                this.setState({
                  upsertItem: {
                    ...upsertItem,
                    RegattaEntryCrewmembers: updatedRegattaEntryCrewmembers,
                  },
                });
              }
            }}
          >
            {regattaEntryCrewmembers.map((
              regattaEntryCrewmember,
              index,
            ) => this.renderUpsertRegattaEntryCrewmembersPartListItem(
              regattaEntryCrewmember,
              index,
            ))}
          </ReactSortable>
        </ListGroup>
      );
    }

    return (
      <ListGroup flush>
        {regattaEntryCrewmembers.map((
          regattaEntryCrewmember,
          index,
        ) => this.renderUpsertRegattaEntryCrewmembersPartListItem(
          regattaEntryCrewmember,
          index,
        ))}
      </ListGroup>
    );
  }

  renderUpsertRegattaEntryCrewmembersPartList() {
    const {
      activeRegattaGroup,
      upsertItem,
    } = this.state;

    const crewQuantity: number = _.get(
      activeRegattaGroup,
      'Group.CrewQuantity',
      0,
    );

    if (upsertItem !== null) {
      let regattaEntryCrewmembers: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>> = [];

      if (crewQuantity > 0) {
        for (let index = 0; index < crewQuantity; index += 1) {
          if (upsertItem.RegattaEntryCrewmembers.length >= index + 1) {
            regattaEntryCrewmembers.push(upsertItem.RegattaEntryCrewmembers[index]);
          } else {
            regattaEntryCrewmembers.push({
              Birthdate: null,
              Email: null,
              Gender: null,
              Membership: null,
              Name: '',
              Phone: null,
              Qualification: null,
              RegattaEntryId: '',
              SortId: index + 1,
              Tshirt: null,
              Weight: null,
            });
          }
        }
      } else {
        regattaEntryCrewmembers = upsertItem.RegattaEntryCrewmembers;
      }

      return this.renderUpsertRegattaEntryCrewmembersPartListList(
        regattaEntryCrewmembers,
      );
    }

    return null;
  }

  renderUpsertRegattaEntryCrewmembersPart() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      mainItem,
      upsertItem,
    } = this.state;

    const crewQuantity: number = _.get(
      activeRegattaGroup,
      'Group.CrewQuantity',
      0,
    );

    return ((mainItem !== null && upsertItem !== null) ? (
      <>
        {this.renderUpsertRegattaEntryCrewmembersPartList()}
        {crewQuantity === 0 && (
          <Row>
            <Col>
              <Button
                className={'mb-3'}
                color={'info'}
                id={'create-regatta-entry-crewmember-button'}
                onClick={() => {
                  const newRegattaEntryCrewmember = {
                    Birthdate: null,
                    Email: null,
                    Gender: null,
                    Membership: null,
                    Name: '',
                    Phone: null,
                    Qualification: null,
                    RegattaEntryId: '',
                    SortId: upsertItem.RegattaEntryCrewmembers.length + 1,
                    Tshirt: null,
                    Weight: null,
                  };

                  if (upsertItem.RegattaEntryCrewmembers) {
                    upsertItem.RegattaEntryCrewmembers.push(newRegattaEntryCrewmember);
                  }

                  this.setState({
                    upsertItem,
                  });
                }}
              >
                {t('Add crewmember')}
              </Button>
            </Col>
            <Col
              className={'d-flex justify-content-end align-items-center mb-3'}
              sm={6}
              xl={3}
            >
              <span className={'mr-2'}>
                {t('Preliminary crew size')}
              </span>
              <InputNumber
                className={'mb-0-i w-25'}
                name={'PreliminaryCrewQuantity'}
                value={upsertItem.PreliminaryCrewQuantity}
              />
            </Col>
          </Row>
        )}
      </>
    ) : null);
  }

  renderRegattaEntryRequirementValueInput({
    index,
    regattaEntryRequirement,
    regattaRequirement,
  }: {
    index: number;
    regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>;
    regattaRequirement: $Model<'RegattaRequirement'>;
  }) {
    const {
      t,
    } = this.props;

    const valueInput: boolean = _.includes(
      [RegattaRequirementType.value, RegattaRequirementType.valueUnique],
      regattaRequirement.Type,
    );

    const fileInput: boolean = _.includes(
      [RegattaRequirementType.file, RegattaRequirementType.fileSource],
      regattaRequirement.Type,
    );

    const selectInput: boolean = _.includes(
      [RegattaRequirementType.select, // regattaRequirementType.selectUnique,
      ],
      regattaRequirement.Type,
    );

    const booleanInput: boolean = _.includes(
      [RegattaRequirementType.boolean],
      regattaRequirement.Type,
    );

    return (
      <>
        {valueInput && (
          <AvField
            className={'mb-0'}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            value={regattaEntryRequirement.Value}
          />
        )}
        {fileInput && (
          <InputFile
            className={'mb-0'}
            fileDirUrl={`${config.CDN_URL}/images/max`}
            labelChangeFile={t('Change file')}
            labelError={t('Error')}
            labelFile={t('File')}
            name={`RegattaEntryRequirements[${index}]Value`}
            onError={ErrorLog.exception}
            required={regattaRequirement.Required}
            uploadUrl={`${config.CDN_URL}/v1.0/upload/image`}
            value={regattaEntryRequirement.Value}
          />
        )}
        {selectInput && (() => {
          let options: Array<{
            label: string;
            value: string;
          }> = [];

          if (regattaRequirement.Source) {
            try {
              options = JSON.parse(regattaRequirement.Source);
            } catch (error) {
              ErrorLog.exception(error as Error);
            }
          }

          return (
            <AvField
              className={'mb-0'}
              name={`RegattaEntryRequirements[${index}]Value`}
              required={regattaRequirement.Required}
              type={'select'}
              value={regattaEntryRequirement.Value}
            >
              {!regattaEntryRequirement.Value && (
                <option />
              )}
              {_.map(
                options,
                (i: {
                  label: string;
                  value: string;
                }) => (
                  <option
                    key={i.value}
                    value={JSON.stringify(i)}
                  >
                    {i.label}
                  </option>
                ),
              )}
            </AvField>
          );
        })()}
        {booleanInput && (
          <InputYesNo
            className={'pt-2'}
            labelFalse={t('No')}
            labelTrue={t('Yes')}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            value={regattaEntryRequirement.Value}
          />
        )}
      </>
    );
  }

  renderRegattaEntryRequirement({
    index,
    regattaEntryRequirement,
    regattaRequirement,
  }: {
    index: number;
    regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>;
    regattaRequirement: $Model<'RegattaRequirement'>;
  }) {
    const name: string | undefined = _.get(
      regattaRequirement,
      'Name',
    );

    const content: string | undefined = _.get(
      regattaRequirement,
      'Content',
    );

    return (
      <div className={'border-input p-3 mb-3'}>
        <h5 className={'mb-1'}>
          {name}
        </h5>
        <div className={'mb-3'}>
          <small>{content}</small>
        </div>
        {regattaEntryRequirement.id && (
          <AvInput
            name={`RegattaEntryRequirements[${index}]id`}
            style={{
              display: 'none',
            }}
            value={regattaEntryRequirement.id}
          />
        )}
        <AvInput
          name={`RegattaEntryRequirements[${index}]RegattaRequirementId`}
          style={{
            display: 'none',
          }}
          value={regattaEntryRequirement.RegattaRequirementId}
        />
        <AvInput
          name={`RegattaEntryRequirements[${index}]RegattaEntryId`}
          style={{
            display: 'none',
          }}
          value={regattaEntryRequirement.RegattaEntryId}
        />
        {this.renderRegattaEntryRequirementValueInput({
          index,
          regattaEntryRequirement,
          regattaRequirement,
        })}
      </div>
    );
  }

  renderUpsertRegattaEntryRequirementsPart() {
    const {
      t,
    } = this.props;
    const {
      mainItem,
      upsertItem,
    } = this.state;

    return (upsertItem !== null
      && mainItem !== null
      && upsertItem.RegattaEntryRequirements
      && upsertItem.RegattaEntryRequirements.length > 0 ? (
        <>
          <hr />
          <h4>{t('Regatta requirements')}</h4>
          <Row>
            {_.map(
              _.sortBy(
                upsertItem.RegattaEntryRequirements,
                'RegattaRequirementId',
              ),
              (
                regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>,
                index: number,
              ) => {
                const regattaRequirement: $Model<'RegattaRequirement'> | undefined = _.find(
                  mainItem.RegattaRequirements,
                  {
                    id: regattaEntryRequirement.RegattaRequirementId,
                  },
                );

                if (regattaRequirement) {
                  return (
                    <Col
                      key={regattaEntryRequirement.id}
                      lg={3}
                      md={6}
                      sm={12}
                    >
                      {this.renderRegattaEntryRequirement({
                        index,
                        regattaEntryRequirement,
                        regattaRequirement,
                      })}
                    </Col>
                  );
                }

                return null;
              },
            )}
          </Row>
        </>
      ) : null);
  }

  renderUpsertDialog() {
    const {
      t,
    } = this.props;
    const {
      mainItem,
      upsertItem,
    } = this.state;

    return (
      <Modal
        backdrop={'static'}
        id={'upsert-regatta-entry-dialog'}
        isOpen={upsertItem !== null}
        size={'xl'}
      >
        <AvForm
          onValidSubmit={async (event: Event, values) => {
            await defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$UpsertRegattaEntry>({
                  ...upsertItem,
                  ...values,
                  PreliminaryCrewQuantity: numberValue(values.PreliminaryCrewQuantity),
                  RegattaEntryCrewmembers: values.RegattaEntryCrewmembers.map((item) => ({
                    ...cleanFormValues(item),
                    Name: `${_.get(
                      item,
                      '_Firstname',
                    )} ${_.get(
                      item,
                      '_Lastname',
                    )}`,
                  })),
                  RegattaEntryRequirements: values.RegattaEntryRequirements
                    ? values.RegattaEntryRequirements.map((item) => cleanFormValues(item))
                    : [],
                  TrackerId: numberValue(values.TrackerId) || null,
                });

                await Services.Api.upsertRegattaEntry(params);

                await this.setState({
                  upsertItem: null,
                });
              },
            });
          }}
        >
          {upsertItem !== null && mainItem !== null && (
            <>
              <ModalHeader>
                <p className={'mb-0'}>
                  {t('Regatta entry')}
                </p>
                <small>
                  {upsertItem.id ? upsertItem.RegistrationNo : t('New')}
                </small>
              </ModalHeader>
              <ModalBody>
                {this.renderUpsertRegattaEntryPart()}
                {this.renderUpsertRegattaEntryCoachPart()}
                {this.renderUpsertRegattaEntryCrewmembersPart()}
                {this.renderUpsertRegattaEntryRequirementsPart()}
                <Row>
                  <Col>
                    <AvField
                      name={'Content'}
                      placeholder={t('Remarks invisible to entry')}
                      rows={2}
                      type={'textarea'}
                      value={upsertItem.Content}
                    />
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter className={'d-flex justify-content-between'}>
                {this.renderRegistrationEditButton()}
                <div>
                  <Button
                    className={'mr-1'}
                    color={'warning'}
                    id={'upsert-regatta-entry-dialog-confirm-button'}
                    type={'submit'}
                  >
                    {t('Save')}
                  </Button>
                  <Button
                    color={'secondary'}
                    id={'upsert-regatta-entry-dialog-close-button'}
                    onClick={() => this.setState({
                      upsertItem: null,
                    })}
                  >
                    {t('Close')}
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </AvForm>
      </Modal>
    );
  }

  renderRegistrationEditButton() {
    const {
      main,
    } = this.props;
    const {
      upsertItem,
    } = this.state;

    if (upsertItem !== null && main.activeRegattaId && main.activeRegionSeasonId) {
      const basePath = getFullItemUrl({
        basePath: '/regatta',
        item: {
          id: main.activeRegattaId,
          RegionSeasonId: main.activeRegionSeasonId,
        },
        mainDomain: config.DOMAIN,
        regions: main.regions,
      });
      const url = `${basePath}?regatta_entry_uid=${upsertItem.Uid}`;

      return (
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={url}
          id={'edit-registraion-button'}
          outline
          target={'_blank'}
        >
          <em className={'fas fa-clipboard'} />
        </Button>
      );
    }

    return null;
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    const RegattaId = main.activeRegattaId;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          iconClass={'icon-list'}
          rightButtons={(
            <>
              {RegattaId !== null && (
                <>
                  <Button
                    className={'mr-1'}
                    color={'secondary'}
                    href={getDocumentUrl({
                      cdnUrl: config.CDN_URL,
                      docRoute: docRoute.adminRegattaTrackers,
                      docType: docType.pdf,
                      id: RegattaId,
                      language: i18next.language as $Language,
                    })}
                    id={'regatta-trackers-button'}
                    outline
                    size={'sm'}
                    target={'_blank'}
                  >
                    <em className={'far fa-file-pdf mr-1'} />
                    {t('Trackers')}
                  </Button>
                  <Button
                    className={'mr-1'}
                    color={'secondary'}
                    href={`${config.CDN_URL}/csv/regatta_entry_crewmembers/${RegattaId}`}
                    id={'regatta-contact-data-button'}
                    outline
                    rel={'noreferrer'}
                    target={'_blank'}
                  >
                    <em className={'far fa-file-csv mr-1'} />
                    {t('Data')}
                  </Button>
                </>
              )}
              <Button
                color={'info'}
                id={'create-regatta-entry-button'}
                onClick={() => {
                  if (RegattaId !== null && mainItem !== null) {
                    const newItem: $UpsertRegattaEntry = {
                      BowNo: null,
                      Club: null,
                      CoachEmail: null,
                      CoachName: null,
                      CoachPhone: null,
                      Content: null,
                      EntryId: null,
                      Harbor: null,
                      Image: null,
                      Lock: false,
                      Model: null,
                      Name: null,
                      PreliminaryCrewQuantity: null,
                      RegattaEntryCrewmembers: [{
                        Birthdate: null,
                        Email: null,
                        Gender: null,
                        Membership: null,
                        Name: '',
                        Phone: null,
                        Qualification: null,
                        RegattaEntryId: '',
                        SortId: 1,
                        Tshirt: null,
                        Weight: null,
                      }],
                      RegattaEntryRequirements: mainItem.RegattaRequirements.map((
                        regattaEntryRequirement,
                      ) => ({
                        RegattaEntryId: '',
                        RegattaRequirementId: regattaEntryRequirement.id,
                        Value: null,
                      })),
                      RegattaGroupId: '',
                      RegattaId,
                      RegistrationNo: '',
                      SailNo: null,
                      TrackerId: null,
                      Uid: 'new',
                    };

                    this.setState({
                      upsertItem: newItem,
                    });
                  }
                }}
                size={'sm'}
                title={t('Add new regatta entry')}
              >
                {t('Add new')}
              </Button>
            </>
          )}
          title={t('Regatta entries')}
        />
      </Card>
    );
  }

  renderData() {
    const {
      t,
    } = this.props;
    const {
      data,
      mainItem,
    } = this.state;

    const regattaGroups: Array<$FullRegattaGroup> = _.get(
      mainItem,
      'RegattaGroups',
      [],
    );

    const sortedRegattaGroups: Array<$FullRegattaGroup> = _.sortBy(
      regattaGroups,
      'Group.Name',
    );

    const groupedData: Array<$RegattaGroupBundle> = sortedRegattaGroups.map((
      regattaGroup: $FullRegattaGroup,
    ) => ({
      CoachEnabled: regattaGroup.CoachEnabled,
      GroupType: regattaGroup.Group.Type as unknown as GroupType,
      Name: regattaGroup.AltName || regattaGroup.Group.Name,
      PreliminaryCrewQuantityEnabled: regattaGroup.PreliminaryCrewQuantityEnabled,
      RatingType: regattaGroup.RatingType === RegattaGroupRatingType.monotype
        ? null
        : regattaGroup.RatingType as unknown as RatingType,
      RegattaEntries: _.orderBy(
        _.filter(
          data,
          {
            RegattaGroupId: regattaGroup.id,
          },
        ),
        ['Name', 'RegistrationNo'],
        ['asc', 'asc'],
      ),
      RegattaGroupId: regattaGroup.id,
    }));

    const ungroupedRegattaGroupBundle: $RegattaGroupBundle = {
      CoachEnabled: false,
      GroupType: GroupType.cruiser,
      Name: t('Ungrouped'),
      PreliminaryCrewQuantityEnabled: false,
      RatingType: null,
      RegattaEntries: _.filter(
        data,
        {
          RegattaGroupId: null,
        },
      ),
      RegattaGroupId: null,
    };

    groupedData.push(ungroupedRegattaGroupBundle);

    return (
      <div id={adminSections[pageId].list}>
        {groupedData.map((regattaGroupBundle: $RegattaGroupBundle) => (mainItem !== null && (
          <RegattaGroupSection
            item={regattaGroupBundle}
            key={regattaGroupBundle.Name}
            onRegattaEntryChecksUpdate={(
              regattaEntryChecks: Array<$Model<'RegattaEntryCheck'>>,
            ) => {
              defaultUpsertItem({
                action: async () => {
                  for (let index = 0; index < regattaEntryChecks.length; index += 1) {
                    const regattaEntryCheck: $Model<'RegattaEntryCheck'> = regattaEntryChecks[index];

                    await Services.Api.updateRegattaEntryCheck(regattaEntryCheck);
                  }

                  await this.getData();
                },
              });
            }}
            onRegattaEntryDelete={(regattaEntry: $FullRegattaEntry) => {
              defaultDeleteItem({
                action: async () => Services.Api.deleteRegattaEntry(regattaEntry),
              });
            }}
            onRegattaEntryEdit={(regattaEntry: $FullRegattaEntry) => this.setState({
              activeRegattaGroup: this.getEntryRegattaGroup(regattaEntry.RegattaGroupId),
              upsertItem: regattaEntry,
            })}
            regatta={mainItem}
          />
        )))}
      </div>
    );
  }

  renderStats() {
    const {
      data,
      mainItem,
    } = this.state;

    return (mainItem !== null ? (
      <RegattaEntriesStatsSection
        data={data}
        id={adminSections[pageId].statistics}
        regatta={mainItem}
      />
    ) : null);
  }

  render() {
    const {
      loading,
    } = this.state;

    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          <Loader
            loading={loading === true}
            overlay
          >
            {this.renderData()}
            {this.renderStats()}
          </Loader>
          {this.renderUpsertDialog()}
        </>
      </Page>
    );
  }
}

export default RegattaEntriesPageView;
